// src/components/DownloadPage.jsx
// Post-purchase download page. Set this as your Stripe redirect URL.
// Drop into src/components/ and add a route in App.jsx:
//   <Route path="/download" element={<DownloadPage />} />

import { useEffect, useRef, useState } from 'react'
import SEOHead from './SEOHead'

// Dynamic version info - will be fetched from Dropbox
const VERSION_INFO_URL = 'https://www.dropbox.com/scl/fi/jx8evktsk7k11zph9ixsy/version-info.json?rlkey=bb90f6nlnfal7hks4l65if1at&st=2am8q4w7&dl=0'

// Token-based authentication for Stripe redirect
const VALID_TOKEN = 'nw_7x77kQmP2vZ' // Hardcoded to match URL token // This should match your Stripe custom parameter

// ─── Particle canvas ──────────────────────────────────────────────────────────
function ParticleCanvas() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let W, H, nodes = [], raf, last = 0

    function resize() {
      W = canvas.width  = window.innerWidth
      H = canvas.height = window.innerHeight
    }
    function init() {
      resize()
      nodes = Array.from({ length: 55 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.8 + 0.6,
      }))
    }
    function draw(ts) {
      if (ts - last < 33) { raf = requestAnimationFrame(draw); return }
      last = ts
      if (document.hidden) { raf = requestAnimationFrame(draw); return }
      ctx.clearRect(0, 0, W, H)
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]
        a.x += a.vx; a.y += a.vy
        if (a.x < 0 || a.x > W) a.vx *= -1
        if (a.y < 0 || a.y > H) a.vy *= -1
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]
          const dx = a.x - b.x, dy = a.y - b.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < 140) {
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.strokeStyle = `rgba(0,212,184,${(1 - dist / 140) * 0.15})`
            ctx.lineWidth = 0.5
            ctx.stroke()
          }
        }
        ctx.beginPath()
        ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(77,157,224,0.6)'
        ctx.fill()
      }
      raf = requestAnimationFrame(draw)
    }

    const onResize = () => {
      resize()
      nodes.forEach(n => { n.x = Math.min(n.x, W); n.y = Math.min(n.y, H) })
    }
    window.addEventListener('resize', onResize)
    init()
    raf = requestAnimationFrame(draw)
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize) }
  }, [])

  return (
    <canvas ref={ref} style={{
      position: 'fixed', inset: 0, zIndex: 0,
      pointerEvents: 'none', opacity: 0.35,
    }} />
  )
}

// ─── Small reusable pieces ────────────────────────────────────────────────────
function LiveDot() {
  return (
    <div style={{
      width: 6, height: 6, borderRadius: '50%',
      background: '#3ecf8e', boxShadow: '0 0 8px #3ecf8e',
      animation: 'dp-pulse 2s ease-in-out infinite',
    }} />
  )
}

function Code({ children }) {
  return (
    <span style={{
      display: 'inline-block',
      background: 'rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 4, padding: '2px 6px',
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 11, color: '#00d4b8',
    }}>{children}</span>
  )
}

function Step({ num, title, desc }) {
  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: 'rgba(0,212,184,0.1)', border: '1px solid rgba(0,212,184,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
        fontWeight: 700, color: '#00d4b8',
      }}>{num}</div>
      <div style={{ flex: 1, paddingTop: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#dce8f5', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'rgba(220,232,245,0.55)', lineHeight: 1.7, fontWeight: 300 }}>{desc}</div>
      </div>
    </div>
  )
}

function InfoCard({ icon, title, desc }) {
  return (
    <div style={{
      background: '#0d1220', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12, padding: 16,
    }}>
      <div style={{ fontSize: 20, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#dce8f5', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: 'rgba(220,232,245,0.55)', lineHeight: 1.6, fontWeight: 300 }}>{desc}</div>
    </div>
  )
}

function TerminalCommand({ command }) {
  const [copied, setCopied] = useState(false)
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  
  return (
    <div style={{
      position: 'relative',
      background: '#0a0f1a',
      border: '1px solid rgba(245,166,35,0.3)',
      borderRadius: 8,
      padding: '16px 50px 16px 16px',
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 13,
      color: '#f5a623',
      lineHeight: 1.6,
      marginTop: 12,
      marginBottom: 8,
    }}>
      {command}
      <button
        onClick={handleCopy}
        style={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          background: copied ? '#3ecf8e' : 'rgba(245,166,35,0.2)',
          border: copied ? '1px solid #3ecf8e' : '1px solid rgba(245,166,35,0.4)',
          borderRadius: 6,
          padding: '6px 12px',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          fontWeight: 600,
          color: copied ? '#0a0f1a' : '#f5a623',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
        onMouseEnter={e => {
          if (!copied) {
            e.currentTarget.style.background = 'rgba(245,166,35,0.3)'
            e.currentTarget.style.borderColor = 'rgba(245,166,35,0.6)'
          }
        }}
        onMouseLeave={e => {
          if (!copied) {
            e.currentTarget.style.background = 'rgba(245,166,35,0.2)'
            e.currentTarget.style.borderColor = 'rgba(245,166,35,0.4)'
          }
        }}
      >
        {copied ? (
          <>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Copied!
          </>
        ) : (
          <>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
            </svg>
            Copy
          </>
        )}
      </button>
    </div>
  )
}

function Divider() {
  return <div style={{ maxWidth: 600, width: '100%', height: 1, background: 'rgba(255,255,255,0.07)', margin: '40px 0' }} />
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function DownloadPage() {
  const [isDownloading, setIsDownloading] = useState(false)
  const [versionInfo, setVersionInfo] = useState(null)
  const [isLoadingVersion, setIsLoadingVersion] = useState(true)
  
  // Check for valid token in URL
  const urlToken = new URLSearchParams(window.location.search).get('token') || 
                   new URLSearchParams(window.location.hash.split('?')[1] || '').get('token')
  
    
  const isAuthenticated = urlToken === VALID_TOKEN
  
  // Fetch version info on component mount
  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        setIsLoadingVersion(true)
        const response = await fetch(VERSION_INFO_URL)
        if (!response.ok) {
          throw new Error('Failed to fetch version info')
        }
        const data = await response.json()
        setVersionInfo(data.latest)
      } catch (error) {
        console.error('Error fetching version info:', error)
        // Fallback to hardcoded values if fetch fails
        setVersionInfo({
          version: '1.0.2',
          downloadUrl: 'https://www.dropbox.com/scl/fi/jx8evktsk7k11zph9ixsy/Netwoke-1.0.2-arm64.dmg?rlkey=bb90f6nlnfal7hks4l65if1at&st=2am8q4w7&dl=0',
          minMacOS: '12.0',
          architecture: 'arm64',
          fileSize: '132.7 MB'
        })
      } finally {
        setIsLoadingVersion(false)
      }
    }
    
    fetchVersionInfo()
  }, [])
  
  // Direct download handler - same as landing page
  const handleDownload = () => {
    if (isDownloading) return // Prevent multiple clicks
    
    setIsDownloading(true)
    
    // Small delay to ensure UI updates before download starts
    setTimeout(() => {
      // Force immediate download using raw Dropbox URL
      const downloadUrl = versionInfo?.downloadUrl || 'https://www.dropbox.com/scl/fi/jx8evktsk7k11zph9ixsy/Netwoke-1.0.2-arm64.dmg?rlkey=bb90f6nlnfal7hks4l65if1at&st=2am8q4w7&dl=0'
      
      // Convert to raw download URL to bypass Dropbox page
      const rawUrl = downloadUrl.replace('dropbox.com/scl/fi', 'dl.dropboxusercontent.com/scl/fi').replace(/&dl=0/, '')
      
      // Use window.open to trigger immediate download
      window.open(rawUrl, '_blank')
    }, 100)
    
    // Reset after 3 seconds (enough time for download to start)
    setTimeout(() => {
      setIsDownloading(false)
    }, 3000)
  }
  
  // If no valid token, show access restricted page
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh', background: '#04060d',
        color: '#dce8f5', fontFamily: "'IBM Plex Sans', sans-serif",
        WebkitFontSmoothing: 'antialiased',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '60px 24px',
      }}>
        <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        
        <div style={{
          background: 'rgba(245,166,35,0.1)',
          border: '1px solid rgba(245,166,35,0.25)',
          borderRadius: 12, padding: '32px',
          textAlign: 'center',
          maxWidth: 400,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'rgba(245,166,35,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, marginBottom: 16,
          }}>🔒</div>
          
          <h2 style={{
            fontSize: 18, fontWeight: 600, color: '#f5a623', marginBottom: 12,
          }}>Access Restricted</h2>
          
          <p style={{
            fontSize: 14, color: 'rgba(220,232,245,0.7)', lineHeight: 1.6,
            marginBottom: 24,
          }}>
            This download page is only accessible after completing the purchase process.
          </p>
          
          <p style={{
            fontSize: 13, color: 'rgba(220,232,245,0.5)',
          }}>
            Please complete your payment to access the Netwoke download.
          </p>
        </div>
      </div>
    )
  }
  return (
    <>
      <SEOHead 
        title="Download Netwoke - Network Monitoring for Mac"
        description="Download Netwoke v1.0.2 for macOS. Real-time network monitoring and privacy protection for Apple Silicon Macs. One-time purchase, no subscription required."
        canonicalUrl="https://netwoke.app/download"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Netwoke",
          "description": "Download Netwoke for macOS - Real-time network monitoring and privacy protection",
          "url": "https://netwoke.app/download",
          "applicationCategory": "SecurityApplication",
          "operatingSystem": "macOS",
          "offers": {
            "@type": "Offer",
            "price": "29",
            "priceCurrency": "USD",
            "validFrom": "2024-01-01",
            "availability": "https://schema.org/InStock",
            "seller": {
              "@type": "Organization",
              "name": "Netwoke",
              "url": "https://netwoke.app"
            }
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "127"
          },
          "author": {
            "@type": "Organization",
            "name": "Netwoke",
            "url": "https://netwoke.app"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Netwoke",
            "url": "https://netwoke.app"
          },
          "downloadUrl": "https://www.dropbox.com/scl/fi/jx8evktsk7k11zph9ixsy/Netwoke-1.0.2-arm64.dmg",
          "softwareVersion": "1.0.2",
          "fileFormat": "application/x-apple-diskimage",
          "memoryRequirements": "8GB RAM recommended",
          "storageRequirements": "500MB free space",
          "processorRequirements": "Apple Silicon M1-M4",
          "datePublished": "2024-01-01",
          "dateModified": "2024-12-01"
        }}
      />
      <div style={{
        minHeight: '100vh', background: '#04060d',
        color: '#dce8f5', fontFamily: "'IBM Plex Sans', sans-serif",
        WebkitFontSmoothing: 'antialiased',
      }}>
        <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

        <ParticleCanvas />

      {/* ── TOP BAR ── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10,
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px',
        background: 'rgba(4,6,13,0.85)', backdropFilter: 'blur(12px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        </div>
      </div>

      {/* ── PAGE CONTENT ── */}
      <div style={{
        position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '120px 24px 80px',
      }}>

        {/* ── HERO ── */}
        <div style={{ maxWidth: 600, width: '100%', textAlign: 'center', marginBottom: 0 }}>

          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 20px', borderRadius: 24,
            background: 'rgba(62,207,142,0.15)', border: '1px solid rgba(62,207,142,0.4)',
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
            color: '#3ecf8e', letterSpacing: '0.1em', textTransform: 'uppercase',
            marginBottom: 28, animation: 'dp-fadeup 0.6s ease both', fontWeight: 600,
          }}>
            Payment confirmed — thank you!
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 700,
            lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: 16,
            animation: 'dp-fadeup 0.6s 0.1s ease both', opacity: 0,
          }}>
            You're ready to{' '}
            <span style={{
              background: 'linear-gradient(90deg, #00d4b8, #4d9de0)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>take control.</span>
          </h1>

          <p style={{
            fontSize: 16, color: 'rgba(220,232,245,0.55)', lineHeight: 1.75,
            fontWeight: 300, marginBottom: 40,
            animation: 'dp-fadeup 0.6s 0.15s ease both', opacity: 0,
          }}>
            Your download is ready. Netwoke v{versionInfo?.version || '1.0.2'} for Apple Silicon Macs.
          </p>

          {/* ── DOWNLOAD BUTTON ── */}
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button onClick={handleDownload} disabled={isDownloading} style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              padding: '18px 36px',
              background: isDownloading ? 'linear-gradient(135deg, #f5a623, #e8940a)' : 'linear-gradient(135deg, #00d4b8, #4d9de0)',
              border: 'none', borderRadius: 14,
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 17, fontWeight: 700, color: '#04060d',
              textDecoration: 'none', letterSpacing: '-0.01em',
              boxShadow: '0 0 40px rgba(0,212,184,0.25), 0 8px 32px rgba(0,0,0,0.4)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              animation: 'dp-fadeup 0.6s 0.2s ease both', opacity: 0,
              cursor: isDownloading ? 'not-allowed' : 'pointer',
            }}
              onMouseEnter={e => { 
                if (!isDownloading) {
                  e.currentTarget.style.transform = 'translateY(-2px)'; 
                  e.currentTarget.style.boxShadow = '0 0 60px rgba(0,212,184,0.35), 0 16px 48px rgba(0,0,0,0.5)' 
                }
              }}
              onMouseLeave={e => { 
                e.currentTarget.style.transform = 'translateY(0)'; 
                e.currentTarget.style.boxShadow = '0 0 40px rgba(0,212,184,0.25), 0 8px 32px rgba(0,0,0,0.4)' 
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {isDownloading ? 'Downloading...' : 'Download from Dropbox'}
            </button>
          </div>   {/* Meta */}
          <div style={{
            marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            color: 'rgba(220,232,245,0.28)', letterSpacing: '0.06em',
            animation: 'dp-fadeup 0.6s 0.25s ease both', opacity: 0,
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              macOS 12+
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span>Apple Silicon (M1–M4)</span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span>v{versionInfo?.version || '1.0.2'}</span>
          </div>
        </div>

        

        <Divider />

        {/* ── INSTALL STEPS ── */}
        <div style={{ maxWidth: 600, width: '100%', animation: 'dp-fadeup 0.6s 0.35s ease both', opacity: 0 }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 600,
            color: '#00d4b8', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20,
          }}>How to install</div>

          <Step num="1" title="Open the DMG file"
            desc={<>Double-click the <Code>Netwoke-1.0.2-arm64.dmg</Code> file in your Downloads folder. A window will appear showing the Netwoke app icon.</>} />
          <Step num="2" title="Drag to Applications"
            desc={<>Drag the Netwoke icon into the <Code>Applications</Code> folder shortcut shown in that window. This installs the app on your Mac.</>} />
          <Step num="3" title="Launch Netwoke"
            desc="Open your Applications folder and double-click Netwoke. On first launch macOS may show a security warning — see below for how to handle it in seconds." />
          <Step num="4" title="Grant network access"
            desc="Netwoke will ask permission to monitor network connections. Click Allow — this is required for the app to work. No data ever leaves your Mac." />
        </div>

        {/* ── GATEKEEPER BOX ── */}
        <div style={{
          maxWidth: 600, width: '100%',
          background: 'rgba(245,166,35,0.05)',
          border: '1px solid rgba(245,166,35,0.25)',
          borderRadius: 12, padding: '20px 22px',
          animation: 'dp-fadeup 0.6s 0.4s ease both', opacity: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(245,166,35,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, flexShrink: 0,
            }}>⚠️</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#f5a623' }}>
                About the macOS security warning
              </div>
              <div style={{ fontSize: 10, color: 'rgba(245,166,35,0.6)',
                fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>
                Gatekeeper · First launch only
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'rgba(220,232,245,0.55)', lineHeight: 1.8, fontWeight: 300 }}>
            <p style={{ marginBottom: 10 }}>
              When you first open Netwoke, macOS may say <em>"Netwoke cannot be opened because it is from an unidentified developer"</em> or <em>"Apple cannot check it for malicious software."</em>
            </p>
            <p style={{ marginBottom: 10 }}>
              This is normal for apps sold outside the Mac App Store. It does <strong style={{ color: '#dce8f5' }}>not</strong> mean the app is unsafe — it simply means we sell directly so we can offer features like firewall control that Apple's sandbox doesn't allow.
            </p>
            <p style={{ marginBottom: 14 }}>
              To open it, use <strong style={{ color: '#dce8f5' }}>either</strong> of these methods:
            </p>
            <p style={{ marginBottom: 10 }}>
              <strong style={{ color: '#dce8f5' }}>Option A — Right-click method (easiest):</strong><br />
              Right-click (or Control-click) the Netwoke icon in Applications → click <Code>Open</Code> → click <Code>Open</Code> again in the dialog. You only need to do this once.
            </p>
            <p>
              <strong style={{ color: '#dce8f5' }}>Option B - System Settings:</strong><br />
              Go to <Code>System Settings → Privacy &amp; Security</Code>, scroll down, and click <Code>Open Anyway</Code> next to the Netwoke entry.
            </p>
            <p>
              <strong style={{ color: '#dce8f5' }}>Option C - Terminal Command:</strong><br />
              Open Terminal and run this command to remove macOS quarantine:
            </p>
            <TerminalCommand command="xattr -r -d com.apple.quarantine /Applications/Netwoke.app" />
          </div>
        </div>

        <Divider />

        {/* ── FOOTER ── */}
        <div style={{
          maxWidth: 600, width: '100%', textAlign: 'center',
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
          color: 'var(--accent-teal)',
          display: 'flex', flexDirection: 'column', gap: 6,
          animation: 'dp-fadeup 0.6s 0.45s ease both', opacity: 0,
        }}>
          <div>Netwoke v{versionInfo?.version || '1.0.2'} · macOS {versionInfo?.minMacOS || '12'} Monterey or later · Apple Silicon</div>
          <div style={{ opacity: 0.6 }}>Netwoke - Where Connection Meets Disclosure.</div>
        </div>

      </div>

      <style>{`
        @keyframes dp-pulse   { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes dp-fadeup  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        @media (max-width: 560px) {
          .dp-cards { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
    </>
  )
}
